import { useState } from 'react';
import type { ElementDefinition } from 'cytoscape';
import { GraphTypeFilter } from './GraphTypeFilter';
import { GraphSearch } from './GraphSearch';

type SwitchableLayout = 'attack-path' | 'layered';

interface Props {
  hiddenNodes: Set<string>;
  hiddenNodeLabels: Map<string, { label: string; type: string }>;
  onUnhideNode: (nodeId: string) => void;
  onUnhideAll: () => void;
  isFocused: boolean;
  onClearFocus: () => void;
  graphLayers?: Map<string, ElementDefinition[]>;
  activeScope?: string;
  onScopeChange?: (scope: string) => void;
  hasCriticalPath: boolean;
  showCriticalPath: boolean;
  onToggleCriticalPath: () => void;
  /** Full element set — used by the entity-type filter to count per type. */
  elements: ElementDefinition[];
  hiddenTypes: Set<string>;
  onToggleType: (type: string) => void;
  onResetTypes: () => void;
  /** Active layout. Persisted to localStorage by GraphPanel. */
  layout: SwitchableLayout;
  onLayoutChange: (layout: SwitchableLayout) => void;
  /** Focus + highlight 1-hop neighborhood for a given node id (search target). */
  onFocusNode: (nodeId: string) => void;
  /** Currently-visible subset of elements (post hide/type-filter) so the
   * search can flag hits that are currently hidden. */
  visibleElements: ElementDefinition[];
  /** Called when the search wants to restore a hidden node before focusing. */
  onUnhideForSearch: (nodeId: string) => void;
}

export function GraphToolbar({
  hiddenNodes,
  hiddenNodeLabels,
  onUnhideNode,
  onUnhideAll,
  isFocused,
  onClearFocus,
  graphLayers,
  activeScope,
  onScopeChange,
  hasCriticalPath,
  showCriticalPath,
  onToggleCriticalPath,
  elements,
  hiddenTypes,
  onToggleType,
  onResetTypes,
  layout,
  onLayoutChange,
  onFocusNode,
  visibleElements,
  onUnhideForSearch,
}: Props) {
  const [showHiddenList, setShowHiddenList] = useState(false);

  const layerKeys = graphLayers ? Array.from(graphLayers.keys()) : [];
  const showScopeSelector = layerKeys.length >= 2 && !!onScopeChange;
  // Distinct-type count drives whether the type filter is worth surfacing.
  // Cheaper than computing the full stats here — GraphTypeFilter does that
  // internally and returns null on ≤1 type anyway.
  const distinctTypes = (() => {
    const seen = new Set<string>();
    for (const el of elements) {
      const isNode = el.group === 'nodes' || (!el.group && !el.data.source);
      if (!isNode) continue;
      const t = el.data.type as string | undefined;
      if (t) seen.add(t);
    }
    return seen.size;
  })();
  const showTypeFilter = distinctTypes >= 2;

  // The graph itself is the trigger for layout + search — both are useful on
  // any non-empty graph, even one with a single entity type.
  const hasContent = elements.some((el) => el.group === 'nodes' || (!el.group && !el.data.source));

  if (!showScopeSelector && !hasCriticalPath && !isFocused && hiddenNodes.size === 0 && !showTypeFilter && !hasContent) {
    return null;
  }

  return (
    <div className="graph-floating-controls">
      {/* Scope selector — multiple AI responses */}
      {showScopeSelector && onScopeChange && (
        layerKeys.length <= 5 ? (
          <div className="graph-layout-selector">
            <button
              className={`graph-layout-btn ${activeScope === 'all' ? 'active' : ''}`}
              onClick={() => onScopeChange('all')}
            >
              All
            </button>
            {layerKeys.map((key, i) => (
              <button
                key={key}
                className={`graph-layout-btn ${activeScope === key ? 'active' : ''}`}
                onClick={() => onScopeChange(key)}
                title={`Response ${i + 1}`}
              >
                R{i + 1}
              </button>
            ))}
          </div>
        ) : (
          <select
            className="graph-scope-select"
            value={activeScope ?? 'all'}
            onChange={(e) => onScopeChange(e.target.value)}
          >
            <option value="all">All responses</option>
            {layerKeys.map((key, i) => (
              <option key={key} value={key}>Response {i + 1}</option>
            ))}
          </select>
        )
      )}

      {/* Critical path toggle */}
      {hasCriticalPath && (
        <button
          className={`graph-layout-btn ${showCriticalPath ? 'active' : ''}`}
          onClick={onToggleCriticalPath}
          title={showCriticalPath ? 'Show all entities equally' : 'Highlight attack path'}
        >
          Path
        </button>
      )}

      {/* Clear focus button */}
      {isFocused && (
        <button
          className="btn-secondary btn-sm graph-clear-focus-btn"
          onClick={onClearFocus}
        >
          Clear focus
        </button>
      )}

      {/* Search — type a label, click to focus + highlight 1-hop neighborhood. */}
      {hasContent && (
        <GraphSearch
          elements={elements}
          visibleElements={visibleElements}
          onFocusNode={onFocusNode}
          onUnhideForSearch={onUnhideForSearch}
        />
      )}

      {/* Layout switcher — attack-path (default) vs layered (by 6-layer ontology). */}
      {hasContent && (
        <div className="graph-layout-selector graph-layout-switcher">
          <button
            className={`graph-layout-btn ${layout === 'attack-path' ? 'active' : ''}`}
            onClick={() => onLayoutChange('attack-path')}
            title="Attack-path layout (dagre — rank by edge flow)"
          >
            Flow
          </button>
          <button
            className={`graph-layout-btn ${layout === 'layered' ? 'active' : ''}`}
            onClick={() => onLayoutChange('layered')}
            title="Layered layout (rank by 6-layer ontology — infrastructure → identity → application → data → threat → decisions)"
          >
            Layers
          </button>
        </div>
      )}

      {/* Entity-type filter (checkboxes per type, grouped by category) */}
      {showTypeFilter && (
        <GraphTypeFilter
          elements={elements}
          hiddenTypes={hiddenTypes}
          onToggleType={onToggleType}
          onResetTypes={onResetTypes}
        />
      )}

      {/* Hidden nodes manager */}
      {hiddenNodes.size > 0 && (
        <div className="graph-hidden-wrapper">
          <button
            className="topbar-icon-btn graph-hidden-btn"
            onClick={() => setShowHiddenList(!showHiddenList)}
            title={`${hiddenNodes.size} hidden node${hiddenNodes.size > 1 ? 's' : ''}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
              <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
              <line x1="1" y1="1" x2="23" y2="23"/>
            </svg>
            <span className="graph-hidden-badge">{hiddenNodes.size}</span>
          </button>
          {showHiddenList && (
            <div className="graph-hidden-dropdown">
              <div className="graph-hidden-dropdown-header">
                <span>Hidden entities</span>
                <button className="btn-link btn-sm" onClick={() => { onUnhideAll(); setShowHiddenList(false); }}>
                  Restore all
                </button>
              </div>
              <div className="graph-hidden-dropdown-list">
                {Array.from(hiddenNodes).map((nodeId) => {
                  const info = hiddenNodeLabels.get(nodeId);
                  return (
                    <div key={nodeId} className="graph-hidden-item">
                      <span className={`node-popover-type type-${info?.type ?? 'unknown'}`}>
                        {info?.type ?? '?'}
                      </span>
                      <span className="graph-hidden-item-label">{info?.label ?? nodeId}</span>
                      <button
                        className="btn-link btn-sm"
                        onClick={() => onUnhideNode(nodeId)}
                      >
                        Restore
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
